// moms-harness:anchor v2
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-B.yaml
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-C.yaml
// JIRA: LOGISPRD-13286, LOGISPRD-13480, LOGISPRD-13833, LOGISPRD-13852, LOGISPRD-13875, LOGISPRD-14571, LOGISPRD-14586, LOGISPRD-14591, LOGISPRD-14923, LOGISPRD-14926
import { type ShipperScope, shipperScopeOf } from '@/components/system/shipperScope'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal, MomsActionBar, Table, TableGroup, message } from '@musinsa/pbo-core'
import { callToAddImportLog } from '@/services/excel/apis'
import {
  ShipperInfoMap,
  SkuAttributeParams,
  SkuAttributeRow,
  useGetSkuAttributeList,
} from '../queries/useGetSkuAttributeList'
import {
  apiErrMessage,
  BULK_CHUNK_SIZE,
  downloadSkuAttributeExcel,
  useBulkRegisterSkuAttributes,
  useUpdateSkuAttributes,
} from '../queries/useModifySkuAttributes'
import { ExcelDownloadReasonModal, toDetailedReason } from './ExcelDownloadReasonModal'
import { COLUMNS, NUMERIC_COLUMNS, PAGE_SIZE } from './consts'
import { withEditCells } from './editCells'
import { toPriceOrNull } from './toPriceOrNull'
import { FAILURE_SAMPLE_CAP, summarizeFailures } from './summarizeFailures'
import { parseSkuAttributeExcel } from './excelImport'
import { downloadUploadTemplate } from './uploadTemplate'
import { withShipperLabels } from './shipperLabels'
import { validateRow } from './validateRow'
import { newRowsShipper, registrationBlockReason, resolveTargetShipper } from './registrationScope'

type Row = SkuAttributeRow & { _rowKey: string }

/**
 * 서버가 빈 상품단위에 넣는 값. **기본값의 정의가 아니라 그 결과를 화면에 되비추기 위한 표시용**이다
 * (정의는 BE `RegisterSkuAttributeService.DEFAULT_UNIT_CODE`).
 *
 * 저장 응답이 이 값을 돌려주지 않아, 저장 성공 후 화면이 직접 맞춰 주지 않으면 **재조회까지
 * 셀은 비어 있고 DB 는 `PCS`** 인 상태가 이어진다. 그 어긋남이 「비웠는데 왜 PCS 냐」를 만든다.
 */
const SERVER_DEFAULT_UNIT_CODE = 'PCS'

/** 목록을 받는 동안 편집·추가를 막을 때의 안내 (LOGISPRD-14923) */
const LOADING_MESSAGE = '목록을 불러오는 중입니다. 잠시 후 다시 시도하세요.'

const matchesQuery = (row: Row, q: string) =>
  !q ||
  Object.values(row).some((v) =>
    String(v ?? '')
      .toLowerCase()
      .includes(q.toLowerCase()),
  )

const emptyRow = (shipperCode: string, shipperInfo: ShipperInfoMap): SkuAttributeRow => ({
  _skuId: '',
  _isNew: true,
  service_type: shipperInfo[shipperCode]?.serviceType ?? '',
  shipper_name: shipperInfo[shipperCode]?.shipperName ?? '',
  shipper_code: shipperCode,
  sku_id: '',
  barcode: '',
  item_code: '',
  item_option: '',
  item_name: '',
  category: '',
  color: '',
  style_number: '',
  // 상품단위는 빈 값으로 둔다 — 서버가 PCS 로 채운다 (consts.ts COLUMNS KDoc).
  // 화면에 'PCS' 를 박으면 기본값 상수가 FE·BE 양쪽에 생겨 한쪽만 바뀌는 날 갈린다
  unit_code: '',
  brand_id: '',
  brand_name: '',
  receivable_price: null,
  payable_price: null,
  created_at: '',
  created_by: '',
  last_modified_at: '',
  last_modified_by: '',
})

interface Props {
  scope?: ShipperScope
  searchParams: SkuAttributeParams
  /** 보고 있는 페이지(1부터). 한 페이지 = 서버 한 페이지(`PAGE_SIZE`)다 (LOGISPRD-14923) */
  page: number
  onPageChange: (page: number) => void
  shipperInfo: ShipperInfoMap
  /**
   * 화주 옵션 목록이 완전한가 — 로딩이 끝났고 MWP·MLT 조회가 모두 성공. 불완전하면 등록 화주유형 가드가 판정하지
   * 않는다(목록에 없는 것이 「MWP·MLT 아님」인지 「못 불러옴」인지 모른다 — `registrationBlockReason` KDoc)
   */
  shipperOptionsComplete: boolean
  /** 필터에서 지금 고른 화주(조회 전 포함). 조회한 화주가 없을 때 등록 대상이 된다 (LOGISPRD-14591) */
  selectedShipperCode?: string
}

export const SkuAttributeTable = ({ searchParams, page, onPageChange, shipperInfo, shipperOptionsComplete, selectedShipperCode, scope: providedScope }: Props) => {
  // 빈 조건에서는 조회하지 않는다 — 조건 없는 전체 조회 방지 (MA02 준용)
  const hasSearchCondition = useMemo(
    () =>
      Object.entries(searchParams).some(
        ([key, value]) => key !== '_seq' && (Array.isArray(value) ? value.length > 0 : value != null && value !== ''),
      ),
    [searchParams],
  )
  const { data, error, isFetching } = useGetSkuAttributeList(searchParams, page, shipperInfo, hasSearchCondition)
  const bulkRegister = useBulkRegisterSkuAttributes()
  const updateRows = useUpdateSkuAttributes()

  const [rows, setRows] = useState<Row[]>([])
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [appendSeq, setAppendSeq] = useState(0)
  const [downloadOpen, setDownloadOpen] = useState(false)
  const [registerConfirmOpen, setRegisterConfirmOpen] = useState(false)
  /**
   * 고쳤지만 아직 저장하지 않은 기존 행. 신규 행은 `_isNew` 로 안다. 페이지를 넘기면 표가 그 페이지 행으로 바뀌어
   * 둘 다 사라지므로, 넘기기 전에 확인한다 (LOGISPRD-14923)
   */
  const [editedKeys, setEditedKeys] = useState<ReadonlySet<string>>(new Set())
  /** 미저장 변경 확인 중인 이동 대상 페이지 */
  const [pendingPage, setPendingPage] = useState<number>()
  /**
   * 이 페이지에서 등록에 성공한 행 수. 등록 성공 행은 `_isNew` 가 풀려 신규 행 수에서 빠지는데, 보고 있는 페이지는 다시
   * 받지 않아(`markListsStale`) 서버 전체 건수에도 아직 없다 — 이 값으로 전체 건수가 등록한 만큼 줄어 보이지 않게 한다
   */
  const [registeredCount, setRegisteredCount] = useState(0)

  // 조회 결과 effect 는 **data 에만** 반응해야 한다 — shipperInfo 에도 반응하면 화주 목록 도착 시 행 전체가 조회
  // 결과로 되돌아가 편집 중인 셀이 사라진다. 그래서 최신 화주 목록은 ref 로 읽는다 (늦게 온 목록은 아래 effect 담당)
  const shipperInfoRef = useRef(shipperInfo)
  shipperInfoRef.current = shipperInfo

  useEffect(() => {
    // 캐시된 조회 결과는 조회 시점의 화주 목록으로 만들어졌다 — 지금 화주 목록으로 두 파생 열을 맞춘다
    setRows(
      withShipperLabels(
        (data?.contents ?? []).map((row, i) => ({ ...row, _rowKey: `r-${i}` })),
        shipperInfoRef.current,
      ),
    )
    setSelected([])
    setEditedKeys(new Set())
    setRegisteredCount(0)
  }, [data])

  // 보던 페이지가 범위를 벗어나면(편집으로 행이 조건에서 빠져 전체 건수가 줄었는데 탭 재진입이 그 페이지를 되살림)
  // 마지막 페이지로 옮긴다 — 그대로 두면 빈 표에 페이저가 「4 / 3」으로 남는다
  useEffect(() => {
    if (!data) return
    const lastPage = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE))
    if (page > lastPage) onPageChange(lastPage)
  }, [data, page, onPageChange])

  // 화주 목록이 조회보다 늦게 오면(SKU ID 단독 URL) 화주타입·화주명이 빈 채로 남는다 — 두 열만 다시 채운다.
  // 행 전체를 data 로 되돌리지 않으므로 사용자가 편집 중인 셀은 유지된다 (LOGISPRD-13875)
  useEffect(() => {
    setRows((prev) => withShipperLabels(prev, shipperInfo))
  }, [shipperInfo])

  // 403 이 이 화면의 가장 흔한 실패다 — 진입 인가가 MASTER 로 좁혀져 있다 (LOGISPRD-13284).
  // 빈 목록으로만 보이면 「데이터가 없다」와 구별되지 않아 사용자가 조건만 계속 바꾸게 된다
  useEffect(() => {
    if (error) message.error(apiErrMessage(error))
  }, [error])

  const filtered = useMemo(() => rows.filter((r) => matchesQuery(r, query)), [rows, query])

  /**
   * 표의 No — **조건 전체에서의 순번**이다 (LOGISPRD-14926). 2페이지 첫 행은 2,001, 3페이지는 4,001 부터다.
   *
   * pbo-core `Table` 은 행에 `no` 가 없으면 받은 행 순서로 1부터 매긴다(`{ no: i + 1, ...row }`). 표에는 이 페이지
   * 행만 넘기므로 페이지마다 1로 돌아갔다. 그래서 행 순서에 앞 페이지 몫(`(page - 1) × PAGE_SIZE`)을 더해 넘긴다.
   *
   * - 이 페이지에 붙인 신규 행은 조회 행 뒤를 이어 매긴다
   * - 목록 검색으로 걸러도 행의 번호는 그대로다 — 걸러진 결과 안에서 다시 매기면 같은 행의 번호가 검색어마다 바뀐다
   * - `rows` 상태에는 넣지 않고 표에 넘길 때만 붙인다 — 넣으면 목록 검색이 No 값에 걸리고, 저장 경로로도 흘러간다
   */
  const numbered = useMemo(() => {
    const offset = (page - 1) * PAGE_SIZE
    const visible = new Set(filtered.map((r) => r._rowKey))
    // `rows` 순서로 돌며 번호를 매기고 걸러진 행만 남긴다 — `filtered` 는 `rows` 의 순서를 지킨 부분집합이라 표 순서는 같다
    return rows.flatMap((r, i) => (visible.has(r._rowKey) ? [{ ...r, no: offset + i + 1 }] : []))
  }, [rows, filtered, page])
  // `Array.includes` 는 O(m) 이라 10,000행 × 1,000선택이면 렌더마다 최대 1,000만 번 비교다.
  // `Set.has` 로 전체를 O(n) 으로 내린다
  const selectedKeySet = useMemo(() => new Set(selected), [selected])
  const selectedRows = useMemo(() => rows.filter((r) => selectedKeySet.has(r._rowKey)), [rows, selectedKeySet])

  /**
   * 셀 편집. **기존 행의 바코드는 막는다** — 바코드는 SCM Hub 채번 키라 바꾸면 다른 SKU 이고,
   * 수정 API 는 그 필드를 아예 받지 않는다. 열어 두면 「고쳤는데 반영이 안 된다」가 된다.
   *
   * 화면은 기존 행 바코드에 입력칸을 아예 그리지 않는다(`withBarcodeCell`, LOGISPRD-14571) — 아래 가드는 그
   * 셀을 거치지 않는 편집 경로가 생겼을 때의 마지막 방어선이다
   */
  const onCellChange = (rowKey: string, dataIndex: string | undefined, value: unknown) => {
    if (!dataIndex) return
    // 받는 중에는 고치지 못하게 한다 — 도착한 응답이 행을 덮어 입력이 확인 없이 사라진다 (LOGISPRD-14923).
    // 낡음 표시된 페이지(저장 뒤)에 돌아오면 캐시 행을 보여 주면서 다시 받으므로 행이 보이는데도 이 상태다
    if (isFetching) {
      message.warning(LOADING_MESSAGE)
      return
    }
    const target = rows.find((r) => r._rowKey === rowKey)
    if (dataIndex === 'barcode' && target && !target._isNew) {
      message.warning('바코드는 변경할 수 없습니다. 다른 바코드는 새 행으로 등록하세요.')
      return
    }
    // **숫자 열은 경계에서 변환한다.** 그리드는 편집값을 문자열로 주는데 행 타입은 `number | null`
    // 이라, 그대로 쓰면 `validateRow` 의 `Number.isFinite('12900')` 이 false 가 되어 정상 입력이
    // 저장에서 막힌다. 엑셀 업로드와 같은 함수를 써 두 경로의 기준이 갈리지 않게 한다
    const next = NUMERIC_COLUMNS.has(dataIndex) ? toPriceOrNull(value) : value
    setRows((prev) => prev.map((r) => (r._rowKey === rowKey ? { ...r, [dataIndex]: next } : r)))
    if (target && !target._isNew) setEditedKeys((prev) => new Set(prev).add(rowKey))
  }

  // 단가·바코드 셀은 화면 셀로 그린다(`withEditCells`). 바코드는 신규 행만 입력칸이다(LOGISPRD-14571).
  // 단가 셀은 pbo-core `amountInput` 대신 화면 셀로 그린다 — `null` 이면 입력칸이 사라져 '+추가' 행에
  // 단가를 넣을 수 없었다 (LOGISPRD-13833, 근거는 priceText.ts KDoc). 콜백을 ref 로 넘겨 컬럼을
  // 한 번만 만든다 — `onCellChange` 는 렌더마다 새로 생기는데 그때마다 컬럼을 바꾸면 표 전체가 다시 그려진다
  const onCellChangeRef = useRef(onCellChange)
  onCellChangeRef.current = onCellChange
  const columns = useMemo(
    () => withEditCells(COLUMNS, (rowKey, dataIndex, text) => onCellChangeRef.current(rowKey, dataIndex, text)),
    [],
  )

  /**
   * 등록 대상 화주 — 조회한 화주가 우선이고, 조회 전이면 필터에서 고른 화주다(`resolveTargetShipper`, LOGISPRD-14591).
   * 행마다 다르면 한 요청이 여러 화주를 건드린다 — 저장은 행에 기록된 화주로 보낸다(`newRowsShipper`)
   */
  const scope = useMemo(() => providedScope ?? shipperScopeOf(AuthStorage.user.get()), [providedScope])
  const targetShipper = resolveTargetShipper(searchParams.shipperCode, selectedShipperCode)

  /** 등록 화주유형 가드 (LOGISPRD-14586) — MWP·MLT 화주만 추가·업로드·신규 저장을 연다. BE 도 400 으로 막는다 */
  const registrationBlocked = registrationBlockReason(targetShipper, shipperInfo[targetShipper]?.serviceType, shipperOptionsComplete, scope)

  const appendRow = () => {
    if (!targetShipper) {
      message.warning('화주명을 선택한 뒤 행을 추가하세요. 등록은 화주 단위로 나갑니다.')
      return
    }
    if (registrationBlocked) {
      message.warning(registrationBlocked)
      return
    }
    const key = `new-${appendSeq}`
    setAppendSeq((n) => n + 1)
    setRows((prev) => [...prev, { ...emptyRow(targetShipper, shipperInfo), _rowKey: key }])
  }

  const fileInputRef = useRef<HTMLInputElement>(null)
  const onImportFile = async (file: File) => {
    if (!targetShipper) {
      message.warning('화주명을 선택한 뒤 업로드하세요. 등록은 화주 단위로 나갑니다.')
      return
    }
    if (registrationBlocked) {
      message.warning(registrationBlocked)
      return
    }
    try {
      const imported = await parseSkuAttributeExcel(file)
      if (!imported.length) {
        message.warning('가져올 데이터 행이 없습니다.')
        return
      }
      setAppendSeq((n) => n + imported.length)
      setRows((prev) => [
        ...prev,
        ...imported.map((item, i) => ({
          ...emptyRow(targetShipper, shipperInfo),
          ...item,
          _rowKey: `imp-${appendSeq + i}`,
        })),
      ])
      callToAddImportLog({ menuCode: 'MA18', apiName: 'MA18_LIST_IMPORT', rowCount: imported.length })
      // 어느 화주로 들어갔는지 적는다 — 조회 뒤 필터만 바꾼 경우 조회한 화주로 들어간다 (resolveTargetShipper)
      message.success(`${imported.length}행을 가져왔습니다 (화주 ${targetShipper}) — 검토 후 선택·저장하세요.`)
    } catch (e) {
      message.error(e instanceof Error ? e.message : '엑셀 파일을 읽을 수 없습니다.')
    }
  }

  /**
   * 저장 — 신규 행은 **등록(`POST /bulk`)**, 기존 행은 **수정(`PUT /{skuId}`)** 으로 갈린다.
   *
   * ⚠️ **등록은 되돌릴 수 없는 외부 쓰기다.** 한 행이 성공하면 SCM Hub 에 SKU 가 만들어지고
   * MOMS 는 지울 수 없다. 그래서
   * 1. 화면이 **먼저 폭·범위를 검증**해 걸린 행은 보내지 않는다 (채번 낭비 방지)
   * 2. 성공 행은 실패 여부와 무관하게 **즉시 `skuId` 를 확정**한다 — 재저장 시 같은 상품을
   *    다시 등록하지 않게. `getOrCreate` 가 idempotent 라 같은 값이 돌아오지만, 확정해 두지
   *    않으면 사용자가 무엇이 저장됐는지 알 수 없다
   */
  const runSave = async () => {
    const newRows = selectedRows.filter((r) => r._isNew)
    const editRows = selectedRows.filter((r) => !r._isNew)

    // ① 화면 검증 — BE 와 같은 기준이다. 걸린 행은 SCM Hub 에 나가지 않는다
    const invalid = [...newRows, ...editRows]
      .map((row) => ({ row, error: validateRow(row) }))
      .filter((v) => v.error != null)
    if (invalid.length) {
      invalid
        .slice(0, FAILURE_SAMPLE_CAP)
        .forEach((v) => message.error(`${v.row.barcode || '(바코드 없음)'}: ${v.error}`))
      if (invalid.length > FAILURE_SAMPLE_CAP) {
        message.error(`외 ${invalid.length - FAILURE_SAMPLE_CAP}건의 입력 오류가 있습니다.`)
      }
      return
    }

    // ⚠️ **상품단위는 비울 수 없다.** BE 계약에 「단위를 비워 둔다」가 없어 빈 값은 PCS 가 된다
    // (BE `RegisterSkuAttributeUsecase.UpdateCommand` KDoc). 조용히 바뀌면 「비웠는데 왜 PCS 냐」가
    // 되므로 저장 전에 알린다. 막지는 않는다 — 사용자가 의도한 저장을 되돌릴 이유가 아니다.
    //
    // `warning` 인 이유 — **사용자가 넣은 값이 다른 값으로 바뀐다**는 사실은 정보성 안내보다
    // 무겁다. 이 화면의 선제 안내 6곳(바코드 변경 불가 · 화주 미선택 · 가져올 행 없음 ·
    // 화주 목록 부분 실패)이 모두 `warning` 이라 기준도 같다 (PR #810 4차 봇 리뷰).
    const blankUnitRows = [...newRows, ...editRows].filter((r) => !r.unit_code.trim())
    if (blankUnitRows.length) {
      message.warning(
        `상품단위가 빈 ${blankUnitRows.length.toLocaleString()}건은 ${SERVER_DEFAULT_UNIT_CODE} 로 저장됩니다. ` +
          '상품단위는 비워 둘 수 없습니다.',
      )
    }

    // ② 등록 — 청크 단위 순차 전송
    const failedRegisterKeys: string[] = []
    if (newRows.length) {
      // 행에 기록된 화주로 보낸다 — 저장 시점의 대상 화주는 조회 전 필터 변경으로 바뀌었을 수 있다.
      // onSaveClick 이 섞임을 먼저 막지만, 화주를 못 정하면 여기서도 보내지 않는다(빈 화주로 채번 요청이 나가지 않게)
      const registering = newRowsShipper(newRows)
      if (registering.error || !registering.shipperCode) {
        message.warning(registering.error ?? '신규 행의 화주를 확인할 수 없습니다. 화주명을 선택한 뒤 다시 추가하세요.')
        return
      }
      const outcome = await bulkRegister.mutateAsync({ shipperCode: registering.shipperCode, rows: newRows })
      // 성공 행의 skuId 를 즉시 확정 — 이후 저장은 수정(PUT) 경로로 간다
      const skuByKey = new Map<string, string>()
      outcome.results.forEach((r, i) => {
        if (r.success && r.skuId) skuByKey.set(newRows[i]._rowKey, r.skuId)
        else failedRegisterKeys.push(newRows[i]._rowKey)
      })
      setRows((prev) =>
        prev.map((r) => {
          const skuId = skuByKey.get(r._rowKey)
          if (!skuId) return r
          // 상품단위도 함께 맞춘다 — 서버가 빈 값을 PCS 로 저장했는데 화면이 빈 칸을 유지하면
          // 재조회까지 두 값이 어긋난다 (SERVER_DEFAULT_UNIT_CODE KDoc)
          const unitCode = r.unit_code.trim() || SERVER_DEFAULT_UNIT_CODE
          return { ...r, _skuId: skuId, sku_id: skuId, _isNew: false, unit_code: unitCode }
        }),
      )
      setRegisteredCount((n) => n + skuByKey.size)
      if (outcome.successCount) message.success(`${outcome.successCount}건 등록 완료`)
      // 실패는 **사유별로 묶어** 안내한다 — 사유마다 후속 행동이 다르고(거래처 미등록은 운영이,
      // 선점은 사용자가 푼다), 행마다 띄우면 전 행 실패 시 토스트가 1,000개 쌓여 화면이 잠긴다
      summarizeFailures(outcome.results.filter((r) => !r.success)).forEach((msg) => message.error(msg))
    }

    // ③ 수정 — 행별 결과 수집
    const failedEditKeys: string[] = []
    if (editRows.length) {
      const results = await updateRows.mutateAsync(editRows)
      const failed = results.filter((r) => !r.ok)
      const succeeded = results.length - failed.length
      if (succeeded) message.success(`${succeeded}건 수정 완료`)
      // 등록과 같은 이유로 성공 행의 상품단위를 서버 결과에 맞춘다
      const okKeys = new Set(results.filter((r) => r.ok).map((r) => editRows[r.index]._rowKey))
      setRows((prev) =>
        prev.map((r) =>
          okKeys.has(r._rowKey) && !r.unit_code.trim()
            ? { ...r, unit_code: SERVER_DEFAULT_UNIT_CODE }
            : r,
        ),
      )
      failed.forEach((r) => failedEditKeys.push(editRows[r.index]._rowKey))
      // 저장된 행은 더 이상 미저장 변경이 아니다 — 실패 행만 남는다
      setEditedKeys((prev) => new Set([...prev].filter((k) => !okKeys.has(k))))
      summarizeFailures(
        failed.map((r) => ({
          barcode: editRows[r.index].barcode || editRows[r.index].sku_id,
          reason: null,
          reasonMessage: apiErrMessage(r.error),
          detail: null,
        })),
      ).forEach((msg) => message.error(msg))
    }

    // **실패 행만 선택을 남긴다** — 사용자가 고쳐서 실패분만 재저장하도록 (MA02 와 같은 동작).
    // 전부 비우면 1,000행 중 30행이 실패했을 때 그 30행을 눈으로 찾아 다시 체크해야 한다
    const failedKeys = new Set([...failedRegisterKeys, ...failedEditKeys])
    setSelected((prev) => prev.filter((k) => failedKeys.has(k)))
  }

  const newRowCount = useMemo(() => selectedRows.filter((r) => r._isNew).length, [selectedRows])

  /**
   * 선택돼 있지만 **목록 검색에 가려 화면에 안 보이는** 행 수.
   *
   * 선택은 `rows`(전체) 기준이고 `filtered`(검색 결과) 기준이 아니다. 그래서 100행을 고른 뒤
   * 검색으로 5행만 남겨도 [저장]은 100행을 보낸다.
   *
   * **선택을 `filtered` 로 좁히지 않는 이유**는 그쪽이 더 위험하기 때문이다 — 사용자가 명시적으로
   * 고른 행이 **화면 필터 때문에 조용히 빠지면** 저장된 줄 알고 넘어간다. 대신 **되돌릴 수 없는
   * 등록 확인 단계에서 그 수를 드러낸다.**
   *
   * pbo-core `Table` 이 `dataSource` 변경 시 숨겨진 행의 선택을 스스로 해제한다면 이 값은 늘 0 이라
   * 아무것도 바뀌지 않는다 — **어느 쪽이든 안전한 방향**이다.
   */
  const hiddenSelectedCount = useMemo(() => {
    const visible = new Set(filtered.map((r) => r._rowKey))
    return selectedRows.filter((r) => !visible.has(r._rowKey)).length
  }, [filtered, selectedRows])

  /**
   * ⚠️ **신규 행이 있으면 확인을 한 단계 둔다.**
   *
   * SCM Hub 채번은 **되돌릴 수 없고 삭제 API 도 없다**(LLD §5.7). 엑셀로 3,000행을 올린 뒤 전체
   * 선택 상태에서 [저장]을 잘못 누르면 3,000개 SKU 가 생기고 화면에서 되돌릴 방법이 없다.
   *
   * MA02 는 그보다 가벼운 **삭제**에도 확인 모달을 둔다 — 되돌릴 수 있는 조작에 확인이 있고
   * 되돌릴 수 없는 조작에 없는 것은 앞뒤가 맞지 않는다.
   *
   * 수정만 있는 저장은 되돌릴 수 있으므로 확인 없이 바로 간다.
   */
  const onSaveClick = () => {
    // 신규 행이 있으면 등록이다 — 확인 모달 전에 막는다 (수정만 있는 저장은 그대로 간다).
    // 판정 화주는 지금 대상 화주가 아니라 신규 행에 기록된 화주다 (LOGISPRD-14591)
    if (newRowCount > 0) {
      const registering = newRowsShipper(selectedRows.filter((r) => r._isNew))
      if (registering.error) {
        message.warning(registering.error)
        return
      }
      const blocked = registrationBlockReason(registering.shipperCode, shipperInfo[registering.shipperCode]?.serviceType, shipperOptionsComplete, scope)
      if (blocked) {
        message.warning(blocked)
        return
      }
    }
    if (newRowCount > 0) {
      setRegisterConfirmOpen(true)
      return
    }
    // `.catch` 를 붙이는 것은 lint(no-void ↔ no-floating-promises) 충돌 회피이기도 하지만,
    // **예기치 못한 예외가 조용히 사라지는 것**을 막는 쪽이 본래 목적이다.
    // 두 mutation 은 내부에서 전 경로를 잡으므로 여기 걸리는 것은 그 밖의 결함이다
    runSave().catch((e: unknown) => message.error(apiErrMessage(e)))
  }

  const requireSelect = selected.length === 0
  const saving = bulkRegister.isPending || updateRows.isPending

  /**
   * 서버 기준 조건 전체 건수 — 페이저가 이것으로 페이지를 나눈다. 이 페이지에 붙인 신규 행은 들어 있지 않다.
   * 다음 페이지를 받는 동안(`data` 없음)은 같은 조회의 마지막 값을 쓴다 — 0 으로 떨어지면 페이저가 「1 / 0」으로 깜빡인다.
   * 같은 조회인지는 **조회 순번(`_seq`)으로** 본다 — 객체 참조로 보면 상위가 조건을 새 객체로 내려 주는 날 조용히 깨진다
   * (LOGISPRD-14926). 순번은 [조회]마다 새로 붙으므로 조건이 같아도 다른 조회의 건수를 빌려 오지 않는다
   */
  // 순번이 있는 조회만 기록한다 — 순번이 없으면(조건 없는 빈 조회) 조회가 나가지 않아 `data` 도 없다. 타입을 `number` 로
  // 좁혀 두 쪽 모두 `undefined` 라 같은 조회로 잘못 보는 경우를 막는다
  const lastTotalRef = useRef<{ seq: number; total: number }>()
  useEffect(() => {
    if (data && searchParams._seq != null) lastTotalRef.current = { seq: searchParams._seq, total: data.totalCount }
  }, [data, searchParams._seq])
  const lastTotal = lastTotalRef.current
  const serverTotal = data?.totalCount ?? (lastTotal != null && lastTotal.seq === searchParams._seq ? lastTotal.total : 0)
  const pendingNewCount = useMemo(() => rows.filter((r) => r._isNew).length, [rows])
  const unsavedCount = useMemo(() => rows.filter((r) => r._isNew || editedKeys.has(r._rowKey)).length, [rows, editedKeys])

  /**
   * 페이지 이동 (LOGISPRD-14923). 미저장 변경이 있으면 확인을 받는다.
   *
   * 저장 중에는 막는다 — 수정 저장 결과(상품단위 PCS 보정·미저장 표시 해제)는 행 키(`r-0` …)로 행에 되돌아오는데,
   * 페이지가 바뀌면 같은 키가 다른 페이지의 행을 가리켜 **남의 행의 상품단위가 바뀐다.** (등록 결과는 신규 행 키
   * `new-*`·`imp-*` 로 돌아와 다른 페이지 행에 닿지 않는다.)
   */
  const requestPage = (next: number) => {
    if (next === page) return
    if (saving) {
      message.warning('저장 중에는 페이지를 이동할 수 없습니다.')
      return
    }
    if (unsavedCount > 0) {
      setPendingPage(next)
      return
    }
    onPageChange(next)
  }

  return (
    <>
      <TableGroup>
        <MomsActionBar
          // 목록 검색은 이 페이지 안에서만 거른다 — 검색 중에는 걸러진 수를, 아니면 조건 전체 건수(+이 페이지 신규·등록 행)를 보인다
          total={query ? filtered.length : serverTotal + pendingNewCount + registeredCount}
          selectedCount={selected.length}
          leftActions={
            // **삭제 버튼이 없는 것은 의도다** — sku_attribute 행을 지워도 SCM Hub 의 SKU 는 남아
            // 「지웠는데 재등록하면 같은 skuId 가 돌아오는」 상태의 의미가 정의돼 있지 않다 (LLD §5.7)
            <Button type="primary" disabled={requireSelect || saving || isFetching} onClick={onSaveClick}>
              저장
            </Button>
          }
          page={page}
          pageSize={PAGE_SIZE}
          pageTotal={serverTotal}
          onPage={requestPage}
          searchPlaceholder="목록 검색"
          onSearch={setQuery}
          rightButtons={[
            { text: '추가', icon: 'plus', action: 'appendRow' },
            { text: '양식 다운로드', icon: 'download', action: 'templateDownload' },
            { text: '엑셀 업로드', icon: 'upload', action: 'excelUpload' },
            { text: '엑셀 다운로드', icon: 'download', action: 'excelDownloadReason' },
          ]}
          onAction={(action) => {
            // 엑셀 업로드는 파일 선택 창부터 열리므로 여기서 먼저 막는다 — 파일을 고른 뒤에 막히면 헛걸음이다.
            // onImportFile 안의 같은 검사는 마지막 방어선이다
            if (action === 'excelUpload' && !targetShipper) {
              message.warning('화주명을 선택한 뒤 업로드하세요. 등록은 화주 단위로 나갑니다.')
              return
            }
            if (action === 'excelUpload' && registrationBlocked) {
              message.warning(registrationBlocked)
              return
            }
            // 저장 중에는 행을 늘리지 않는다. 청크 전송 중 추가된 행은 진행 중인 요청에 들어가지
            // 못하는데, 저장이 끝나면 선택만 비워져 **저장된 줄 알고 넘어가기 쉽다**.
            // 다운로드는 저장과 무관하므로 막지 않는다
            if (saving && (action === 'appendRow' || action === 'excelUpload')) return
            // 받는 중에 붙인 행은 응답이 도착하면 사라진다 — 셀 편집과 같은 이유로 막는다
            if (isFetching && (action === 'appendRow' || action === 'excelUpload')) {
              message.warning(LOADING_MESSAGE)
              return
            }
            if (action === 'appendRow') appendRow()
            if (action === 'excelUpload') fileInputRef.current?.click()
            if (action === 'excelDownloadReason') setDownloadOpen(true)
            // 빈 양식이라 조회·저장과 무관하다 — 저장 중에도 막지 않는다
            if (action === 'templateDownload') {
              downloadUploadTemplate().catch(() => message.error('양식 파일을 만들지 못했습니다.'))
            }
          }}
        />
        <Table
          columns={columns}
          dataSource={numbered as unknown as Record<string, unknown>[]}
          selectable
          domain="logistics"
          selectedRowKeys={selected}
          onSelectChange={setSelected}
          cellEdit={{ onChange: onCellChange, onAction: () => undefined }}
        />
      </TableGroup>
      {/* 다운로드 사유 모달 — MA02 것을 **복사해 이 폴더에 둔다.** 재사용하면 MA02 를 고치는 사람이
          이 화면도 바뀐다는 것을 모른다 (컴포넌트 KDoc 의 격리 근거) */}
      <ExcelDownloadReasonModal
        open={downloadOpen}
        onCancel={() => setDownloadOpen(false)}
        onConfirm={async (reason) => {
          setDownloadOpen(false)
          try {
            await downloadSkuAttributeExcel(searchParams, toDetailedReason(reason))
            message.success('다운로드 완료')
          } catch (e) {
            message.error(apiErrMessage(e))
          }
        }}
      />
      <Modal
        open={registerConfirmOpen}
        title="신규 SKU 등록 확인"
        okText="등록"
        cancelText="취소"
        onOk={() => {
          setRegisterConfirmOpen(false)
          runSave().catch((e: unknown) => message.error(apiErrMessage(e)))
        }}
        onCancel={() => setRegisterConfirmOpen(false)}
      >
        신규 SKU {newRowCount.toLocaleString()}건을 등록합니다.
        <br />
        <strong>등록된 SKU 는 삭제할 수 없습니다.</strong> 계속할까요?
        {hiddenSelectedCount > 0 && (
          <>
            <br />
            <br />
            <strong>
              ⚠️ 선택한 {selectedRows.length.toLocaleString()}건 중 {hiddenSelectedCount.toLocaleString()}건은 목록 검색에
              가려 화면에 보이지 않습니다.
            </strong>
          </>
        )}
      </Modal>
      <Modal
        open={pendingPage != null}
        title="저장하지 않은 변경"
        okText="이동"
        cancelText="취소"
        onOk={() => {
          if (pendingPage != null) onPageChange(pendingPage)
          setPendingPage(undefined)
        }}
        onCancel={() => setPendingPage(undefined)}
      >
        저장하지 않은 변경 {unsavedCount.toLocaleString()}건이 있습니다.
        <br />
        페이지를 이동하면 <strong>변경 내용이 사라집니다.</strong> 이동할까요?
      </Modal>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          // `void` 를 붙이면 no-void 가 에러다 — 두 규칙이 서로 충돌한다. MA02 와 같은 형태로 둔다.
          // onImportFile 이 내부에서 전 경로를 try/catch 하므로 reject 되지 않는다
          if (file) onImportFile(file)
        }}
      />
    </>
  )
}

export { BULK_CHUNK_SIZE }
